/**
 * CustomPainelCalendarios — quem entra em cada pasta da agenda
 * (ficha #27, Fase 2; portas em `rota_agenda_calendarios.py`).
 *
 * ─── A COISA QUE ESTA TELA TEM DE DIZER SEM RODEIO ─────────────────────────
 *
 * **Pasta sem ninguém na lista é ABERTA, não fechada.** É o contrário do que a
 * intuição diz ao ver uma lista vazia numa tela de acesso, e é exatamente o tipo
 * de mentira que a lei do acesso descreve: nada quebra, e um dia alguém vê o que
 * não devia — ou, aqui, o contrário: alguém acha que fechou e não fechou.
 *
 * Por isso o estado aparece escrito em cada pasta, com a palavra inteira, e não
 * como uma lista vazia que o olho interpreta sozinho.
 *
 * ─── O QUE ELA NÃO FAZ ─────────────────────────────────────────────────────
 *
 * Não cria pasta (a 104 traz as duas que existem) e não é o "o que exibir" — a
 * caixinha de marcar fica na própria agenda, é gosto de cada um, e não tem nada
 * a ver com permissão.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { api } from '@/lib/api';

interface Calendario {
  id: number;
  calendario: string;
  nome: string;
  tipo: string;
  papel: string;
}

interface Acesso {
  id?: number;
  perfil_id: number | null;
  usuario_id: number | null;
  papel: string;
}

interface Props {
  usuarios: { id: number; nome: string }[];
  perfis: { id: number; nome: string }[];
  BarraSalvar: React.ComponentType<{
    mudancas: string[];
    salvando: boolean;
    aoSalvar: () => void;
    aoDescartar: () => void;
  }>;
}

/** Do mais fraco ao mais forte — a mesma ordem do backend, e a explicação que
 *  a pessoa precisa para escolher sem adivinhar. */
const PAPEIS = [
  { valor: 'livre_ocupado', nome: 'Só ocupado', ajuda: 'Vê que o dia está tomado, sem título nem detalhe' },
  { valor: 'leitor', nome: 'Lê', ajuda: 'Vê os compromissos da pasta' },
  { valor: 'editor', nome: 'Edita', ajuda: 'Vê e mexe nos compromissos' },
  { valor: 'dono', nome: 'Dono', ajuda: 'Edita e decide quem entra' },
];

export function CustomPainelCalendarios({ usuarios, perfis, BarraSalvar }: Props) {
  const [calendarios, setCalendarios] = useState<Calendario[]>([]);
  const [escolhido, setEscolhido] = useState<number | null>(null);
  const [salvos, setSalvos] = useState<Acesso[]>([]);
  const [rascunho, setRascunho] = useState<Acesso[]>([]);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const cals = await api.get<Calendario[]>('/agenda/calendarios');
        setCalendarios(cals);
        if (cals.length) setEscolhido(cals[0].id);
      } catch {
        setErro('Não consegui ler as pastas da agenda.');
      }
    })();
  }, []);

  const carregarAcessos = useCallback(async (id: number) => {
    try {
      const linhas = await api.get<Acesso[]>(`/agenda/calendarios/${id}/acessos`);
      setSalvos(linhas);
      setRascunho(linhas);
      setErro(null);
    } catch {
      setErro('Não consegui ler quem entra nesta pasta.');
    }
  }, []);

  useEffect(() => {
    if (escolhido !== null) carregarAcessos(escolhido);
  }, [escolhido, carregarAcessos]);

  const nomeDe = useCallback(
    (a: Acesso) =>
      a.perfil_id !== null
        ? perfis.find((p) => p.id === a.perfil_id)?.nome ?? `Perfil ${a.perfil_id}`
        : usuarios.find((u) => u.id === a.usuario_id)?.nome ?? `Pessoa ${a.usuario_id}`,
    [perfis, usuarios],
  );

  const mudancas = useMemo(() => {
    const chave = (a: Acesso) => `${a.perfil_id ?? 'u' + a.usuario_id}:${a.papel}`;
    const antes = new Set(salvos.map(chave));
    const agora = new Set(rascunho.map(chave));
    const lista: string[] = [];
    // O aviso do vazio vem PRIMEIRO, porque é o que muda o efeito de tudo o
    // mais: uma lista que esvazia não fecha a pasta, abre.
    if (salvos.length > 0 && rascunho.length === 0) {
      lista.push('A pasta volta a ficar ABERTA a quem já vê a agenda');
    }
    rascunho.filter((a) => !antes.has(chave(a))).forEach((a) =>
      lista.push(`Entra: ${nomeDe(a)} (${PAPEIS.find((p) => p.valor === a.papel)?.nome})`),
    );
    salvos.filter((a) => !agora.has(chave(a))).forEach((a) =>
      lista.push(`Sai: ${nomeDe(a)}`),
    );
    return lista;
  }, [salvos, rascunho, nomeDe]);

  async function salvar() {
    if (escolhido === null) return;
    setSalvando(true);
    try {
      await api.put(`/agenda/calendarios/${escolhido}/acessos`, {
        acessos: rascunho.map((a) => ({
          perfil_id: a.perfil_id,
          usuario_id: a.usuario_id,
          papel: a.papel,
        })),
      });
      await carregarAcessos(escolhido);
    } catch {
      setErro('Não consegui salvar. Nada foi mudado.');
    } finally {
      setSalvando(false);
    }
  }

  function acrescentar(tipo: 'perfil' | 'usuario', id: number) {
    if (!id) return;
    const existe = rascunho.some((a) =>
      tipo === 'perfil' ? a.perfil_id === id : a.usuario_id === id,
    );
    if (existe) return;
    setRascunho([
      ...rascunho,
      {
        perfil_id: tipo === 'perfil' ? id : null,
        usuario_id: tipo === 'usuario' ? id : null,
        papel: 'leitor',
      },
    ]);
  }

  const pasta = calendarios.find((c) => c.id === escolhido);
  const aberta = rascunho.length === 0;

  return (
    <div>
      <h6 className="mb-1">Calendários da agenda</h6>
      <p className="small text-secondary">
        Quem enxerga cada pasta. O que cada pessoa escolhe <em>exibir</em> é
        outra coisa, e fica na própria agenda.
      </p>

      {erro && <div className="alert alert-danger py-2 small">{erro}</div>}

      <div className="btn-group btn-group-sm mb-3" role="group">
        {calendarios.map((c) => (
          <button
            key={c.id}
            type="button"
            className={`btn ${c.id === escolhido ? 'btn-theme' : 'btn-outline-theme'}`}
            onClick={() => setEscolhido(c.id)}
          >
            {c.nome}
          </button>
        ))}
      </div>

      {pasta && (
        <>
          <BarraSalvar
            mudancas={mudancas}
            salvando={salvando}
            aoSalvar={salvar}
            aoDescartar={() => setRascunho(salvos)}
          />

          {/* O estado escrito por extenso. Lista vazia em tela de acesso é lida
              como "ninguém entra", e aqui significa o oposto. */}
          <div className={`alert py-2 small ${aberta ? 'alert-warning' : 'alert-info'}`}>
            <i className={`bi ${aberta ? 'bi-unlock' : 'bi-lock'} me-2`}></i>
            {aberta ? (
              <>
                <strong>Pasta aberta.</strong> Todo mundo que já vê a agenda
                enxerga “{pasta.nome}”. Ela só passa a ser restrita quando
                alguém entrar na lista abaixo.
              </>
            ) : (
              <>
                <strong>Pasta restrita.</strong> Só quem está na lista enxerga
                “{pasta.nome}”. Esvaziar a lista a deixa aberta de novo.
              </>
            )}
          </div>

          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Quem</th>
                <th style={{ width: '12rem' }}>Pode</th>
                <th style={{ width: '3rem' }}></th>
              </tr>
            </thead>
            <tbody>
              {rascunho.map((a, i) => (
                <tr key={`${a.perfil_id ?? 'u'}-${a.usuario_id ?? ''}-${i}`}>
                  <td>
                    <i
                      className={`bi ${a.perfil_id !== null ? 'bi-people' : 'bi-person'} me-2 text-secondary`}
                    ></i>
                    {nomeDe(a)}
                    {a.perfil_id !== null && (
                      <span className="badge text-bg-light ms-2">perfil</span>
                    )}
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      value={a.papel}
                      onChange={(e) => {
                        const copia = [...rascunho];
                        copia[i] = { ...a, papel: e.target.value };
                        setRascunho(copia);
                      }}
                    >
                      {PAPEIS.map((p) => (
                        <option key={p.valor} value={p.valor} title={p.ajuda}>
                          {p.nome}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger"
                      title="Tirar da lista"
                      onClick={() => setRascunho(rascunho.filter((_, j) => j !== i))}
                    >
                      <i className="bi bi-x-lg"></i>
                    </button>
                  </td>
                </tr>
              ))}
              {rascunho.length === 0 && (
                <tr>
                  <td colSpan={3} className="text-secondary small">
                    Ninguém na lista — é isso que deixa a pasta aberta.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="row g-2">
            <div className="col-sm-6">
              <select
                className="form-select form-select-sm"
                value=""
                onChange={(e) => acrescentar('perfil', Number(e.target.value))}
              >
                <option value="">+ Acrescentar um perfil…</option>
                {perfis.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nome}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-sm-6">
              <select
                className="form-select form-select-sm"
                value=""
                onChange={(e) => acrescentar('usuario', Number(e.target.value))}
              >
                <option value="">+ Acrescentar uma pessoa…</option>
                {usuarios.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.nome}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <p className="small text-secondary mt-3 mb-0">
            <i className="bi bi-info-circle me-1"></i>
            Quem entra pelo perfil <em>e</em> pelo nome fica com o papel mais
            forte dos dois.
          </p>
        </>
      )}
    </div>
  );
}

export default CustomPainelCalendarios;
